"use client"

import * as React from "react"
import { Link, useLocation } from "react-router-dom"
import { ChevronRight } from "lucide-react"

import {
  renderRouteIconWithAnimRef,
  sidebarNavIconShellClass,
  useSidebarNavIconAnimation,
} from "@/components/Sidebar/sidebarNavIconAnimation"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarMenu,
  SidebarSeparator,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useCloseMobileSidebar,
} from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"

function pathIsActive(pathname: string, url: string): boolean {
  if (pathname === url) return true
  if (url === "/") return false
  return pathname.startsWith(`${url}/`)
}

export type NavMainItem = {
  title: string
  url: string
  /** Same animated route icons as legacy sidebar (`routes.tsx` / `SidebarNavRouteRow`). */
  icon?: React.ReactNode
  isActive?: boolean
  /** Items with the same group sit together; a full-width divider separates groups (no headings). */
  group?: string
  items?: {
    title: string
    url: string
  }[]
}

/** Slightly larger rows, label, and icons when the desktop sidebar is expanded (icon rail unchanged). */
const navMainExpandedButton =
  "group-data-[state=expanded]:h-10 group-data-[state=expanded]:gap-2.5 group-data-[state=expanded]:text-[0.9375rem] group-data-[state=expanded]:leading-snug group-data-[state=expanded]:[&_svg]:size-5"

const navMainExpandedSubLink =
  "group-data-[state=expanded]:h-8 group-data-[state=expanded]:px-2.5 group-data-[state=expanded]:text-[0.9375rem] group-data-[state=expanded]:[&>svg]:size-4"

const navIconShellClass = cn(
  sidebarNavIconShellClass,
  "[&_svg]:size-4 group-data-[state=expanded]:[&_svg]:size-5"
)

function NavMainLeafItem({
  item,
  active,
}: {
  item: NavMainItem
  active: boolean
}) {
  const { iconAnimRef, linkHandlers } = useSidebarNavIconAnimation()
  const closeMobileSidebar = useCloseMobileSidebar()

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={item.title}
        isActive={active}
        className={navMainExpandedButton}
      >
        <Link to={item.url} onClick={closeMobileSidebar} {...linkHandlers}>
          {item.icon != null ? (
            <span className={navIconShellClass}>
              {renderRouteIconWithAnimRef(item.icon, iconAnimRef)}
            </span>
          ) : null}
          <span>{item.title}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

function NavMainCollapsibleItem({
  item,
}: {
  item: NavMainItem & { items: NonNullable<NavMainItem["items"]> }
}) {
  const { pathname } = useLocation()
  const { iconAnimRef, linkHandlers } = useSidebarNavIconAnimation()
  const closeMobileSidebar = useCloseMobileSidebar()

  return (
    <Collapsible
      asChild
      defaultOpen={item.isActive}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            className={navMainExpandedButton}
            {...linkHandlers}
          >
            {item.icon != null ? (
              <span className={navIconShellClass}>
                {renderRouteIconWithAnimRef(item.icon, iconAnimRef)}
              </span>
            ) : null}
            <span>{item.title}</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.items.map((subItem) => {
              const subActive = pathIsActive(pathname, subItem.url)
              return (
              <SidebarMenuSubItem key={subItem.title}>
                <SidebarMenuSubButton
                  asChild
                  isActive={subActive}
                  className={navMainExpandedSubLink}
                >
                  <Link to={subItem.url} onClick={closeMobileSidebar}>
                    <span>{subItem.title}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
              )
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

export function NavMain({ items }: { items: NavMainItem[] }) {
  const { pathname } = useLocation()
  // Consecutive items with the same group render together, a divider between groups.
  const groups: { group?: string; items: NavMainItem[] }[] = []
  for (const item of items) {
    const last = groups[groups.length - 1]
    if (last && last.group === item.group) last.items.push(item)
    else groups.push({ group: item.group, items: [item] })
  }

  return (
    <>
    {groups.map((group, gi) => (
    <React.Fragment key={group.group ?? `group-${gi}`}>
    {gi > 0 ? (
      // Full sidebar width: the content area has no side padding, only the groups do.
      <SidebarSeparator data-nav-divider="" className="!mx-0 w-full" />
    ) : null}
    <SidebarGroup data-nav-group={group.group ?? ""}>
      <SidebarMenu>
        {group.items.map((item) => {
          const hasSubItems = Boolean(item.items?.length)

          if (!hasSubItems) {
            const active =
              item.isActive ?? pathIsActive(pathname, item.url)

            return (
              <NavMainLeafItem key={item.title} item={item} active={active} />
            )
          }

          return (
            <NavMainCollapsibleItem
              key={item.title}
              item={item as NavMainItem & { items: NonNullable<NavMainItem["items"]> }}
            />
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
    </React.Fragment>
    ))}
    </>
  )
}
